// Updated CompanyPage: reads id from the route param and calls getCompanyById(id)
// Passes numeric id to CompanyEvent feed; null-checked UI
import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Avatar,
  Button,
  Typography,
  Grid,
  Paper,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import { getCompanyById } from '../controller.tsx';
import CompanyEvent from '../Components/CompanyEvent';

const CompanyPage = () => {
  const { id: idParam } = useParams(); // route param /company/:id
  const [company, setCompany] = useState({
    id: null,
    name: null,
    address: null,
    description: null,
    phone: null,
    email: null,
    logo: null,
    banner: null,
  });
  // eslint-disable-next-line
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    const load = async () => {
      setLoading(true);
      try {
        if (typeof getCompanyById === 'function') {
          const res = await getCompanyById(idParam);
          if (!mountedRef.current) return;
          if (res && typeof res === 'object' && !Array.isArray(res)) {
            const {
              id = null,
              name = null,
              address = null,
              description = null,
              phone = null,
              email = null,
              logo = null,
              banner = null,
            } = res;
            setCompany({ id, name, address, description, phone, email, logo, banner });
          } else {
            setCompany((prev) => ({ ...prev }));
          }
        } else {
          // eslint-disable-next-line no-console
          console.warn('getCompanyById is not a function');
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error fetching company:', err);
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    };

    load();
    return () => { mountedRef.current = false; };
  }, [idParam]);
  const { id, name, address, description, phone, email, logo, banner } = company;
  const displayName = name || 'Unknown Company';
  const displayDescription = description || 'No description available.';
  const hasAnyContact = Boolean(address || phone || email);

  // numeric id for the feed; fallback to 0 per your prior instruction
  const companyIdForFeed = id != null ? Number(id) : (idParam ? Number(idParam) : 0);

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary' }}>
      <Box
        sx={{
          height: 220,
          backgroundImage: banner ? `url(${banner})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundColor: banner ? 'transparent' : 'action.hover',
          position: 'relative',
        }}
      >
      <Box
        sx={{
          position: 'absolute',
          bottom: 16,
          left: '50%',
          transform: 'translateX(-50%)',
        }}
      >
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={() => {
            const isLocal =
              window.location.hostname === 'localhost' ||
              window.location.hostname === '127.0.0.1';

            window.location.href = isLocal
              ? 'http://localhost:3000'
              : 'https://www.aroundeverything.com/';
          }}
        >
          Home
        </Button>
      </Box>

        <Box sx={{ position: 'absolute', right: 12, top: 12 }}>
          <IconButton aria-label="more" color="inherit">
            <MoreHorizIcon />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ px: { xs: 2, md: 6 }, pt: 0 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Avatar
              src={logo || undefined}
              alt={displayName}
              sx={{
                width: 110,
                height: 110,
                mt: -6,
                border: '4px solid',
                borderColor: 'background.paper',
                bgcolor: !logo ? 'primary.main' : undefined,
              }}
            >
              {!logo && (displayName ? displayName.charAt(0).toUpperCase() : '?')}
            </Avatar>
          </Grid>

          <Grid item xs>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                {displayName}
              </Typography>
{/*
              <Button variant="contained" color="primary" sx={{ ml: 1 }} disabled={loading}>
                Follow
              </Button>
              <Button variant="outlined" sx={{ ml: 1 }} disabled={!email && !phone}>
                Message
              </Button>
*/}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {displayDescription}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                About
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {displayDescription}
              </Typography>
            </Paper>

            {hasAnyContact && (
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Contact
                </Typography>
                <List dense>
                  {address && (
                    <ListItem>
                      <ListItemIcon>
                        <LocationOnIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={address} />
                    </ListItem>
                  )}
                  {phone && (
                    <ListItem>
                      <ListItemIcon>
                        <PhoneIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={phone} />
                    </ListItem>
                  )}
                  {email && (
                    <ListItem>
                      <ListItemIcon>
                        <EmailIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={email} />
                    </ListItem>
                  )}
                </List>
              </Paper>
            )}
          </Grid>

          <Grid item xs={12} md={8}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Company details
            </Typography>

            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {displayDescription}
              </Typography>
            </Paper>

            <Paper sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Below is the company event feed (events connected via company_events). The feed calls getEventsByCompanyId(companyId).
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" sx={{ mb: 2 }}>
          Event feed
        </Typography>

        <CompanyEvent companyId={companyIdForFeed} />
      </Box>
    </Box>
  );
};

export default CompanyPage;